import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiUrl, getAuthHeaders } from '../../config/api';
import { googleStaffAuth } from '../../utils/googleStaffAuth';
import { useThemedStyles } from '../../theme/useThemedStyles';

export default function CalendarSharingPanel({ token }: { token: string }) {
  const { t } = useTranslation();
  const theme = useThemedStyles();
  const [sharing, setSharing] = useState<any>(null);
  const [calendars, setCalendars] = useState<{ id: string; name: string }[]>([]);
  const [calendarId, setCalendarId] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const request = useCallback(async (path: string, method = 'GET', body?: object) => {
    const response = await fetch(getApiUrl(path), { method, headers: getAuthHeaders(token), ...(body ? { body: JSON.stringify(body) } : {}) });
    const data = response.status === 204 ? null : await response.json();
    if (!response.ok) throw new Error(data?.error || 'requestFailed');
    return data;
  }, [token]);
  const refresh = useCallback(async () => {
    const connection = await request('/calendar/sharing');
    setSharing(connection); setCalendarId(connection.sharingCalendarId || '');
  }, [request]);
  useEffect(() => { refresh().catch(e => setError(e.message)); }, [refresh]);
  const perform = async (action: () => Promise<unknown>) => {
    setBusy(true); setError('');
    try { await action(); await refresh(); }
    catch (e) { setError(e instanceof Error ? e.message : 'requestFailed'); }
    finally { setBusy(false); }
  };
  const inputStyle: React.CSSProperties = { ...theme.input, padding: 8, width: '100%' };
  return <section style={{ ...theme.card, padding: 16, marginTop: 20 }}>
    <h3>{t('staff.sharingTitle')}</h3>
    {error && <p role="alert">{t(`staff.errors.${error}`, { defaultValue: t('staff.errors.requestFailed') })}</p>}
    <p>{t('staff.sharingHelp')}</p>
    <p>{sharing?.sharingCalendarName || t('staff.noCalendar')}</p>
    <button disabled={busy} style={theme.button} onClick={() => perform(() => googleStaffAuth('/calendar/authorize', token))}>{t('staff.connectOwner')}</button>{' '}
    <button disabled={busy || !sharing?.ownerConnected} onClick={() => perform(async () => setCalendars(await request('/calendar/list')))}>{t('staff.loadCalendars')}</button>
    {calendars.length > 0 && <div style={{ marginTop: 12 }}>
      <label>{t('staff.sharingCalendar')}<select value={calendarId} onChange={e => setCalendarId(e.target.value)} style={inputStyle}>
        <option value="">{t('staff.selectCalendar')}</option>{calendars.map(c => <option value={c.id} key={c.id}>{c.name}</option>)}
      </select></label>
      <button disabled={busy || !calendarId} onClick={() => perform(() => request('/calendar/sharing', 'PUT', { calendarId }))}>{t('staff.save')}</button>
    </div>}
    <p>{t('staff.manualUploadHelp')}</p>
    {sharing?.accesses?.length > 0 && <>
      <ul>{sharing.accesses.map((a: any) => <li key={a.id}>{a.email}: {t(`staff.status.${a.status}`)}{a.errorCode ? ` — ${t(`staff.errors.${a.errorCode}`)}` : ''}</li>)}</ul>
      <button disabled={busy} onClick={() => perform(() => request('/calendar/sharing/retry-removals', 'POST'))}>{t('staff.retryRemovals')}</button>
    </>}
  </section>;
}
